import {
  Unstable_NumberInput as BaseNumberInput,
  type NumberInputProps,
  numberInputClasses
} from '@mui/base/Unstable_NumberInput';
import { Box, Card, List, ListItem, styled, Typography } from '@mui/material';
import { type FC, type ForwardedRef, forwardRef, useEffect, useState } from 'react';
import {
  type AmsPartModel,
  listAmsPartsAutoComplete,
  listBrewersAutoComplete,
  type VisitAmsPartModel
} from '../../models';
import { ImageViewer } from '../Dialogs';
import { AutoCompleteFormControl, type FetchOptionsFunction } from '../FormComponents/AutoCompleteFormControl';

const StyledInputRoot = styled('div')(
  ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 400;
  border-radius: 8px;
  color: ${theme.palette.mode === 'dark' ? 'white' : theme.colors.primary.main};
  background: ${theme.palette.mode === 'dark' ? 'transparent' : '#fff'};
  border: 1px solid ${theme.colors.alpha.black[30]};
  box-shadow: none;
  display: grid;
  grid-template-columns: 1fr 19px;
  grid-template-rows: 1fr 1fr;
  overflow: hidden;
  column-gap: 8px;
  padding: 4px;

  &.${numberInputClasses.focused} {
    border-color: ${theme.colors.primary.main};
    box-shadow: none;
  }

  &:hover {
    border-color: ${theme.colors.primary.main};
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`
);

const StyledInputElement = styled('input')(
  ({ theme }) => `
  /* font-size: 0.875rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.5; */
  grid-column: 1/2;
  grid-row: 1/3;
  color: ${theme.palette.mode === 'dark' ? 'white' : theme.colors.primary.main};
  background: inherit;
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  outline: 0;
`
);

const StyledButton = styled('button')(
  ({ theme }) => `
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  appearance: none;
  padding: 0;
  width: 19px;
  height: 19px;
  font-family: system-ui, sans-serif;
  font-size: 0.875rem;
  line-height: 1;
  box-sizing: border-box;
  background: ${theme.palette.mode === 'dark' ? theme.colors.avani.aGrey : '#fff'};
  border: 0;
  color: ${theme.palette.mode === 'dark' ? 'white' : theme.colors.primary.main};
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    background: ${theme.colors.avani.magenta};
    border-color: ${theme.palette.mode === 'dark' ? 'white' : theme.colors.primary.main};
    cursor: pointer;
  }

  &.${numberInputClasses.incrementButton} {
    grid-column: 2/3;
    grid-row: 1/2;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border: 1px solid;
    border-bottom: 0;
    border-color: ${theme.palette.mode === 'dark' ? 'white' : theme.colors.primary.main};
    background: ${theme.colors.avani.magenta};
    color: white;

    &:hover {
      cursor: pointer;
    }
  }

  &.${numberInputClasses.decrementButton} {
    grid-column: 2/3;
    grid-row: 2/3;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border: 1px solid;
    border-color: ${theme.palette.mode === 'dark' ? 'white' : theme.colors.primary.main};
    background: ${theme.colors.avani.magenta};
    color: white;
  }

  &:hover {
    cursor: pointer;
  }

  & .arrow {
    transform: translateY(-1px);
  }

  & .arrow {
    transform: translateY(-1px);
  }
`
);

export const CustomNumberInput = forwardRef(function CustomNumberInput(
  props: NumberInputProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  return (
    <BaseNumberInput
      slots={{
        root: StyledInputRoot,
        input: StyledInputElement,
        incrementButton: StyledButton,
        decrementButton: StyledButton
      }}
      slotProps={{
        incrementButton: {
          children: '▴'
        },
        decrementButton: {
          children: '▾'
        }
      }}
      {...props}
      ref={ref}
    />
  );
});

const StyledCard = styled(Card)(
  ({ theme }) => `
    border-radius: 16px;
    padding: 16px;
    border: 1px solid ${theme.colors.alpha.black[30]};
    background-color: transparent;
    box-shadow: none;
    width: 100%;

    &:hover {
      border-color: ${theme.colors.primary.main}
    }
`
);

type AmsPartCardProps = {
  selectPart: (v: VisitAmsPartModel) => void;
  updateExistingPart: (v: VisitAmsPartModel, oldId: string) => void;
  part: VisitAmsPartModel;
  existing?: boolean;
};

type VisitAmsPartBrewerModel = {
  id: string;
  name: string;
};

export const AmsPartCard: FC<AmsPartCardProps> = ({ part, selectPart, updateExistingPart, existing }) => {
  const [quantity, setQuantity] = useState(existing ? part.quantity : 1);
  const [timeSpent, setTimeSpent] = useState(existing ? (part.timeSpent ? part.timeSpent : 60) : 60);
  const [amsPart, setAmsPart] = useState<VisitAmsPartModel | null>(existing ? part : null);
  const _brewer = { id: String(part.amsPartBrewerId), name: String(part.amsPartBrewerName) };
  const [brewer, setBrewer] = useState<VisitAmsPartBrewerModel | null>(
    !!existing && !!part.amsPartBrewerName && !!part.amsPartBrewerId ? _brewer : null
  );

  useEffect(() => {
    if (!amsPart || amsPart.amsPartId === 'newPart') return;

    selectPart({
      id: amsPart.id,
      amsPartBrewerId: amsPart.amsPartBrewerId,
      amsPartBrewerName: amsPart.amsPartBrewerName,
      amsPartType: amsPart.amsPartType,
      amsPartMainCategory: amsPart.amsPartMainCategory,
      amsPartId: amsPart.amsPartId,
      quantity: amsPart.quantity ?? 1,
      timeSpent: amsPart.timeSpent ?? 60,
      medias: amsPart.medias
    });
    //todo:v2-changes
  }, [amsPart]);

  useEffect(() => {
    if (!quantity || !timeSpent || part.amsPartId === '') return;

    setAmsPart({
      id: amsPart ? amsPart.id : part.id,
      amsPartBrewerName: amsPart ? amsPart?.amsPartBrewerName : '',
      amsPartBrewerId: amsPart ? amsPart?.amsPartBrewerId : '',
      amsPartId: amsPart ? amsPart.amsPartId : '',
      amsPartType: amsPart ? amsPart.amsPartType : '',
      amsPartMainCategory: amsPart ? amsPart.amsPartMainCategory : '',
      quantity: quantity ? quantity : 1,
      timeSpent: timeSpent ? timeSpent : 60,
      medias: amsPart?.medias
    });
    //todo:v2-changes
  }, [quantity, timeSpent]);

  return (
    <StyledCard>
      <Box>
        <Box display={'flex'}>
          <Typography
            variant={'h4'}
            sx={{ display: 'block', textAlign: 'right', minWidth: '100px', padding: '20px 18px 0 0' }}
          >
            Brewer
          </Typography>
          <AutoCompleteFormControl
            id="brewer"
            label="Select Brewer"
            singleLabelOption="Brewer"
            multipleLabelOption="Brewer"
            multiple={false}
            helperText=" "
            Grid={12}
            disabled={!!brewer && !!existing}
            defaultValue={brewer?.name && brewer.id ? { id: brewer.id, name: brewer.name } : undefined}
            onChangeValue={(value) => {
              if (!value) return;

              setBrewer({
                id: String((value as VisitAmsPartBrewerModel).id),
                name: String((value as VisitAmsPartBrewerModel).name)
              } as VisitAmsPartBrewerModel);
            }}
            fetchOptionsFunction={listBrewersAutoComplete as FetchOptionsFunction<VisitAmsPartBrewerModel>}
            getOptionLabel={(option: VisitAmsPartBrewerModel) => option.name}
            renderOption={(option: VisitAmsPartBrewerModel) => option.name}
            getOptionSelected={(option: VisitAmsPartBrewerModel, value: any) => option.id === value.id}
            getOptionKey={(option: VisitAmsPartBrewerModel) => option.id}
          />
        </Box>
        <Box display={'flex'}>
          <Typography
            variant={'h4'}
            sx={{ display: 'block', textAlign: 'right', minWidth: '100px', padding: '20px 18px 0 0' }}
          >
            AMS Part
          </Typography>
          <AutoCompleteFormControl
            id="amsPart"
            label="Select AMS part"
            singleLabelOption="AMS part"
            multipleLabelOption="AMS part"
            multiple={false}
            helperText=" "
            Grid={12}
            defaultValue={
              part && part.amsPartId !== 'newPart'
                ? ({
                    id: part.amsPartId,
                    type: part.amsPartType,
                    mainCategory: part.amsPartMainCategory
                  } as AmsPartModel)
                : undefined
            }
            onChangeValue={(value) => {
              if (value) {
                if (amsPart) {
                  setAmsPart({
                    id: amsPart.id,
                    amsPartId: (value as AmsPartModel).id,
                    amsPartType: (value as AmsPartModel).type,
                    quantity: amsPart.quantity,
                    timeSpent: amsPart.timeSpent,
                    medias: amsPart.medias
                  } as VisitAmsPartModel);
                } else {
                  setAmsPart({
                    id: part.id,
                    amsPartId: (value as AmsPartModel).id,
                    amsPartType: (value as AmsPartModel).type,
                    quantity: 1,
                    timeSpent: 60
                  } as VisitAmsPartModel);
                }
              } else {
                if (!amsPart || amsPart.amsPartId === '') {
                  return;
                } else {
                  updateExistingPart(
                    {
                      id: amsPart.id,
                      amsPartType: 'deleted',
                      amsPartId: amsPart.amsPartId,
                      quantity: amsPart.quantity ?? 1,
                      timeSpent: amsPart.timeSpent ?? 60
                    } as VisitAmsPartModel,
                    amsPart.id
                  );
                }
              }
            }}
            fetchOptionsFunction={(search) => listAmsPartsAutoComplete(String(search), String(brewer?.id))}
            getOptionLabel={(option: AmsPartModel) => `${option.mainCategory} - ${option.type}`}
            renderOption={(option: AmsPartModel) => `${option.mainCategory} - ${option.type}`}
            getOptionSelected={(option: AmsPartModel, value: any) => option.id === value.id}
            getOptionKey={(option: AmsPartModel) => option.id}
          />
        </Box>
        <Box display={'flex'} marginTop={2}>
          <Typography
            variant={'h4'}
            sx={{ display: 'block', textAlign: 'right', minWidth: '100px', padding: '18px 18px 0 0' }}
          >
            Quantity
          </Typography>
          <CustomNumberInput
            aria-label="Quantity"
            placeholder="Type a number…"
            value={quantity}
            onChange={(e, val) => setQuantity(val ? val : 1)}
          />
        </Box>
        <Box display={'flex'} marginTop={2}>
          <Typography
            variant={'h4'}
            sx={{ display: 'block', textAlign: 'right', minWidth: '100px', padding: '18px 18px 0 0' }}
          >
            Time spent
          </Typography>
          <CustomNumberInput
            aria-label="Time Spent"
            placeholder="Type a number…"
            value={timeSpent}
            onChange={(e, val) => setTimeSpent(val ? val : 60)}
          />
        </Box>
        <Box display={'flex'} marginTop={2} minHeight="100px">
          <Typography
            variant={'h4'}
            sx={{ display: 'block', textAlign: 'right', minWidth: '100px', padding: '18px 18px 0 0' }}
          >
            Multimedia
          </Typography>
          <List disablePadding style={{ display: 'flex' }}>
            {part.medias?.map((item, i) => {
              return (
                <ListItem
                  disablePadding
                  key={item.contentUrl + i}
                  sx={{
                    display: 'inline-block',
                    width: '80px',
                    height: '80px',
                    marginRight: '8px'
                  }}
                >
                  <ImageViewer index={i} imageSrc={part.medias ?? []} avatarWidth={80} avatarHeight={80} />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Box>
    </StyledCard>
  );
};
